import { type JSX, useCallback, useEffect, useState } from "react";
import type { IDataSources } from "@via/utils/interfaces.tsx";
import { useIntegrationTokens } from "@via/api/integration-tokens.api.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import { tokenBasedConnectionOptions } from "@via/utils/form-validators.tsx";
import { useForm } from "react-hook-form";
import { ROUTES } from "@via/utils/constants.tsx";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useBaseSpaceProjectList } from "@via/pages/data/data-sources/data-source.api.tsx";
import { useBaseSpaceConnection } from "@via/hooks/useBaseSpaceConnection.tsx";
import { ServiceToken, ServiceTokenListItem, ServiceTokenType } from "common";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import Drawer from "@via/components/drawer.tsx";
import CreateIntegrationTokenForm from "@via/pages/account/profile/integration-tokens/components/create-integration-token-form.tsx";

interface TokenBasedDataSourceConnectorProps {
  source: IDataSources;
}

interface TokenBasedConnection {
  tokenId: number;
}

const getTokenType = (sourceId: string) => {
  if (sourceId === "BaseSpace") {
    return "baseSpace";
  } else {
    return "";
  }
};

const createToken = {
  id: 0,
  name: "New Account",
  tokenType: ServiceTokenType.BaseSpace,
  credentialData: {
    token: "*",
  },
  ownerEmail: "",
  selected: null,
  createdAt: new Date(),
  updatedAt: new Date(),
  lastModifiedUser: 1,
};

export function TokenBasedDataSourceConnector({
  source,
}: TokenBasedDataSourceConnectorProps): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setConnectionCredentials } = useBaseSpaceConnection();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [cumulativeTokens, setCumulativeTokens] = useState<
    ServiceTokenListItem[]
  >([createToken]);
  const [tokensPage, setTokensPage] = useState(0);
  const tokenOptionSize = 10;

  const tokenType = getTokenType(source.id);
  const { isPending: isSavedTokensLoading, data: savedTokenData } =
    useIntegrationTokens(
      tokensPage,
      tokenOptionSize,
      [],
      [{ id: "tokenType", value: tokenType }]
    );

  const fetchMoreIntegrationTokens = useCallback(() => {
    setTokensPage(currentPage => {
      const nextPage = currentPage + 1;
      const maxPage =
        Math.ceil((savedTokenData?.total ?? 0) / tokenOptionSize) - 1;
      return nextPage <= maxPage ? nextPage : currentPage;
    });
  }, [savedTokenData?.total, tokenOptionSize]);

  useEffect(() => {
    if (savedTokenData?.data) {
      setCumulativeTokens([...cumulativeTokens, ...savedTokenData.data]);
    }
  }, [savedTokenData?.data, tokensPage]);

  const {
    mutateAsync: getBaseSpaceProjectList,
    isPending: isGetBaseSpaceProjectListLoading,
  } = useBaseSpaceProjectList();

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<TokenBasedConnection>({
    mode: "onChange",
  });
  const savedTokenIdIdValue = watch("tokenId");

  useEffect(() => {
    if (savedTokenIdIdValue === 0) {
      setIsDrawerOpen(true);
    }
  }, [savedTokenIdIdValue]);

  const handleTokenConnectionFormSubmit = async (
    formPayload: TokenBasedConnection
  ) => {
    await getBaseSpaceProjectList({ tokenId: formPayload.tokenId });
    setConnectionCredentials({ tokenId: formPayload.tokenId });

    const datasetId = searchParams.get("datasetId");
    const url = datasetId
      ? `${ROUTES.DATA_SOURCE_PROVIDER_BASESPACE}?datasetId=${datasetId}`
      : `${ROUTES.DATA_SOURCE_PROVIDER_BASESPACE}`;
    navigate(url);
  };

  const handleCancel = () => {
    navigate(ROUTES.DATA);
  };

  const handleTokenCreate = (tokenResponse: ServiceToken) => {
    if (tokenResponse?.id) {
      setValue("tokenId", tokenResponse.id);
    }
  };

  return (
    <div>
      <form
        onSubmit={e => {
          e.preventDefault();
          handleSubmit(handleTokenConnectionFormSubmit)(e);
        }}>
        <div className="max-w-[850px] mx-auto space-y-6 pb-5 pt-3">
          <div className="max-w-[850px] space-y-6 pb-5 pt-6">
            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">
                Choose BaseSpace Account&nbsp;
                <span className="text-red-400">*</span>
              </label>
              <div className="mt-2" data-testid="tokensSelector">
                <SelectComponent
                  control={control}
                  name="tokenId"
                  rules={tokenBasedConnectionOptions.tokenId}
                  formatOptionLabel={option => {
                    return option.value === 0 ? (
                      <div className="flex flex-row items-center font-semibold">
                        <PlusCircleIcon className="h-5 w-5 mr-2" />{" "}
                        {option.label}
                      </div>
                    ) : (
                      option.label
                    );
                  }}
                  options={
                    cumulativeTokens?.map(({ name, id }) => ({
                      label: name,
                      value: id,
                    })) ?? []
                  }
                  onFetchMoreOptions={fetchMoreIntegrationTokens}
                  isLoading={isSavedTokensLoading}
                  placeholder="Choose Account"
                />
                {errors.tokenId && (
                  <span className="text-xs text-rose-500">
                    {errors.tokenId.message}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-shrink-0 justify-end py-4">
            <button
              data-testid="cloudConnectorCancelButton"
              onClick={handleCancel}
              disabled={isSavedTokensLoading}
              className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
              Cancel
            </button>
            <button
              data-testid="cloudConnectorSubmitButton"
              type="submit"
              disabled={isSavedTokensLoading}
              className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              <Spinner
                loading={
                  isSavedTokensLoading || isGetBaseSpaceProjectListLoading
                }>
                Connect
              </Spinner>
            </button>
          </div>
        </div>
      </form>
      <Drawer
        id="create-integration-token-data"
        open={isDrawerOpen}
        setOpen={setIsDrawerOpen}>
        <CreateIntegrationTokenForm
          setOpen={setIsDrawerOpen}
          givenTokenType={ServiceTokenType.BaseSpace}
          onSuccess={handleTokenCreate}
        />
      </Drawer>
    </div>
  );
}
